import * as XLSX from "xlsx";
import {
  MisaOutward,
  MisaOutwardDetail,
  MisaInward,
  MisaInwardDetail,
  MisaPurchase,
  MisaPurchaseDetail,
  PrintInfo,
} from "../types/misa";
import { generatePrintHTML } from "../utils/generatePrintHTML";
import { generatePrintHTMLNhapKho } from "../utils/generatePrintHTMLNhapKho";
import { generatePrintHTMLNhapHang } from "../utils/generatePrintHTMLNhapHang";
import { getBranchNameById } from "../config/branches";

const SKU_LOCATION_SHEET_URL =
  "https://docs.google.com/spreadsheets/d/1eec7Nc7X2kMohpCLJjurOUcNuDu89OpNPDRg4yXdq0c/export?format=csv&gid=0";

// Fetch SKU locations
const fetchSkuLocations = async (): Promise<Map<string, string>> => {
  try {
    const response = await fetch(SKU_LOCATION_SHEET_URL);
    if (!response.ok) return new Map();

    let csvText = await response.text();
    csvText = csvText.replace(/^\uFEFF/, "");
    const lines = csvText.split(/\r?\n/);
    const locationMap = new Map<string, string>();

    const parseCSVLine = (line: string): string[] => {
      const result: string[] = [];
      let current = "";
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === "," && !inQuotes) {
          result.push(current);
          current = "";
        } else {
          current += char;
        }
      }
      result.push(current);
      return result;
    };

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const columns = parseCSVLine(line);
      if (columns.length >= 4) {
        const skuCode = columns[1].replace(/^['"]|['"]$/g, "").trim();
        const location = columns[3].replace(/^['"]|['"]$/g, "").trim();
        if (skuCode && location) {
          locationMap.set(skuCode, location);
        }
      }
    }

    return locationMap;
  } catch (error) {
    console.error("Error fetching SKU locations:", error);
    return new Map();
  }
};

// Generate HTML content
const generateHTMLContent = (
  loaiPhieu: "xuat" | "nhapkho" | "nhaphang",
  row: any,
  sortedDetails: any[],
  skuLocations: Map<string, string>
): string => {
  const headerWithNames = {
    ...row,
    BranchName: row.BranchName || getBranchNameById(row.BranchID || ""),
  };

  const printInfo: PrintInfo = {
    header: headerWithNames,
    details: sortedDetails,
    currentDate: new Date().toLocaleDateString("vi-VN"),
    skuLocations,
  };

  if (loaiPhieu === "xuat") return generatePrintHTML(printInfo);
  if (loaiPhieu === "nhapkho") return generatePrintHTMLNhapKho(printInfo);
  return generatePrintHTMLNhapHang(printInfo);
};

// Parse HTML to Excel data, robustly map columns and barcode
const parseHTMLToExcelData = (
  htmlContent: string,
  details: any[],
  skuLocations: Map<string, string>,
  inventorries?: any[]
): any[][] => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlContent;
  const excelData: any[][] = [];
  // Company info: split into 3 lines (company, address, MST)
  const companyInfo = tempDiv.querySelector(".company-info");
  if (companyInfo) {
    // Remove all tags, then split by MST:
    let text = companyInfo.innerHTML
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();
    // Try to split into 3 parts: company, address, MST
    // Example: "CÔNG TY ...PHARCECOĐịa chỉ: Thôn Bá Khê, Xã Văn Giang, Tỉnh Hưng YênMST: 0901155224"
    const mstIdx = text.indexOf("MST:");
    let mst = "";
    if (mstIdx !== -1) {
      mst = text.slice(mstIdx).trim();
      text = text.slice(0, mstIdx).trim();
    }
    // Find 'Địa chỉ:'
    const addrIdx = text.indexOf("Địa chỉ:");
    let company = text;
    let addr = "";
    if (addrIdx !== -1) {
      company = text.slice(0, addrIdx).trim();
      addr = text.slice(addrIdx).trim();
    }
    if (company) excelData.push([company]);
    if (addr) excelData.push([addr]);
    if (mst) excelData.push([mst]);
  }
  excelData.push([]);
  // Title
  const title = tempDiv.querySelector(".title")?.textContent?.trim();
  if (title) excelData.push([title]);
  excelData.push([]);
  // Date info: tách ngày và số phiếu thành 2 dòng
  const dateInfo = tempDiv.querySelector(".date-info");
  if (dateInfo) {
    let text = dateInfo.textContent?.trim() || "";
    // Tìm vị trí "Số:" để tách
    const soIdx = text.indexOf("Số:");
    if (soIdx !== -1) {
      const ngay = text.slice(0, soIdx).trim();
      const so = text.slice(soIdx).trim();
      if (ngay) excelData.push([ngay]);
      if (so) excelData.push([so]);
    } else {
      if (text) excelData.push([text]);
    }
  }
  excelData.push([]);
  // Info rows
  tempDiv.querySelectorAll(".info-row").forEach((row) => {
    const text = row.textContent?.trim();
    if (text) excelData.push([text]);
  });
  excelData.push([]);
  // Header bảng 2 dòng: dòng 1 merge 2 cột cuối thành 'Cảm quan', dòng 2 là 'Đạt', 'Không đạt'
  const headerRow1 = [
    "STT",
    "Mã SKU",
    "Barcode",
    "Tên hàng hóa",
    "Vị trí",
    "Lô",
    "HSD",
    "ĐVT",
    "SL",
    "Cảm quan",
  ];
  const headerRow2 = ["", "", "", "", "", "", "", "", "", "Đạt", "Không đạt"];
  excelData.push(headerRow1);
  excelData.push(headerRow2);
  // Dòng dữ liệu chi tiết
  if (details && details.length > 0) {
    details.forEach((item, idx) => {
      excelData.push([
        idx + 1,
        item.SKUCode || "",
        item.Barcode || item.BarCode || item.MainBarcode || "",
        item.InventoryItemName || "",
        skuLocations.get(item.SKUCode || "") || "",
        item.LotNo || "",
        new Date(item.ExpiryDate).toLocaleDateString("vi-VN"),
        item.UnitName || item.Unit || "",
        item.Quantity || item.SL || "",
        item.QualifyQty || item.Dat || "",
        item.NotQualifyQty || item.KhongDat || "",
      ]);
    });
    // Dòng tổng
    const tongSL = details.reduce(
      (sum, item) => sum + (item.Quantity || item.SL || 0),
      0
    );
    excelData.push(["", "", "", "", "", "", "", "Tổng", tongSL || "", "", ""]);
  }
  excelData.push([]);
  excelData.push([]);
  // Footer date: chuyển sang cột I, J, K
  let footerDateText = "";
  tempDiv.querySelectorAll("div").forEach((div) => {
    const text = div.textContent?.trim() || "";
    if (text.includes("Ngày ") || text.includes("tháng ")) {
      footerDateText = text;
    }
  });
  if (footerDateText) {
    // Tách thành 3 phần: "Ngày ...", "tháng ...", "năm ..."
    const match = footerDateText.match(/(Ngày[^n]*)tháng([^n]*)năm(.*)/);
    if (match) {
      const colI = match[1]?.trim() || "";
      const colJ = ("tháng" + (match[2] || "")).trim();
      const colK = ("năm" + (match[3] || "")).trim();
      const row: string[] = Array(7).fill("");
      row[7] = colI;
      row[8] = colJ;
      row[9] = colK;
      excelData.push(row);
    } else {
      // Nếu không tách được thì để nguyên ở cột I
      const row: string[] = Array(7).fill("");
      row[7] = footerDateText;
      excelData.push(row);
    }
  }
  excelData.push([]);
  // Signatures: mỗi cụm chiếm 3 ô
  const signatures: string[] = [];
  tempDiv.querySelectorAll(".signature-section").forEach((sig) => {
    const title = sig.querySelector(".signature-title")?.textContent?.trim();
    if (title) signatures.push(title);
  });
  if (signatures.length > 0) {
    const row1 = Array(12).fill("");
    const row2 = Array(12).fill("");
    for (let i = 0; i < signatures.length; i++) {
      row1[i * 3 + 1] = signatures[i];
      row2[i * 3 + 1] = "(Ký, họ tên)";
    }
    excelData.push(row1);
    excelData.push(row2);
  }
  return excelData;
};

// Main Excel export service
export const exportExcelServiceNhapKho = async (
  row: MisaOutward | MisaInward | MisaPurchase,
  details: MisaOutwardDetail[] | MisaInwardDetail[] | MisaPurchaseDetail[],
  loaiPhieu: "xuat" | "nhapkho" | "nhaphang",
  inventorries?: any[]
): Promise<void> => {
  if (!row?.RefID) {
    throw new Error("Không thể lấy chi tiết phiếu - thiếu RefID");
  }
  try {
    // Get SKU locations
    const skuLocations = await fetchSkuLocations();
    // const sortedDetails = sortDetailsByLocation(details, skuLocations);
    // sort theo tên hang hóa
    const sortedDetails = [...details].sort((a, b) => {
      const nameA = (a.InventoryItemName || "").toLowerCase();
      const nameB = (b.InventoryItemName || "").toLowerCase();
      return nameA.localeCompare(nameB, "vi");
    });

    // NOTE: Barcode cần lấy trong list inventorries từ SKUCode để lấy Barcode
    // Mapping Barcode from inventorries by SKUCode before export
    if (inventorries && inventorries.length > 0) {
      sortedDetails.forEach((item) => {
        const inv = inventorries.find(
          (it: any) =>
            it.SKUCode === item.SKUCode || it.skuCode === item.SKUCode
        );
        if (inv) {
          item.Barcode = inv?.Barcode || inv?.BarCode || inv?.MainBarcode || "";
        } else {
          item.Barcode = "";
        }
        // item.Location = skuLocations.get(item.SKUCode || "") || "";
      });
    }

    // Generate HTML content
    const htmlContent = generateHTMLContent(
      loaiPhieu,
      row,
      sortedDetails,
      skuLocations
    );
    // Parse HTML to Excel data (with barcode)
    const excelData = parseHTMLToExcelData(
      htmlContent,
      sortedDetails,
      skuLocations,
      inventorries
    );
    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(excelData);
    // Set column widths
    worksheet["!cols"] = [
      { wch: 8 }, // A
      { wch: 15 }, // B
      { wch: 15 }, // C (Barcode)
      { wch: 40 }, // D
      { wch: 15 }, // E
      { wch: 15 }, // F
      { wch: 18 }, // G
      { wch: 12 }, // H
      { wch: 12 }, // I
      { wch: 12 }, // J
      { wch: 12 }, // K
      { wch: 12 }, // L
      { wch: 12 }, // M
      { wch: 12 }, // N
      { wch: 12 }, // O
    ];
    // Merge cells
    const merges: any[] = [];
    excelData.forEach((rowData, idx) => {
      const text = (rowData[0] || "").toString();
      if (text.includes("PHIẾU") || text.includes("phiếu")) {
        merges.push({ s: { r: idx, c: 0 }, e: { r: idx, c: 14 } }); // Merge A-O
      }
      if (text.includes("Ngày ") || text.includes("tháng ")) {
        merges.push({ s: { r: idx, c: 10 }, e: { r: idx, c: 14 } }); // Merge K-O
      }
    });
    // Merge header 'Cảm quan' cho 2 cột cuối dòng đầu bảng
    merges.push({ s: { r: 0, c: 9 }, e: { r: 0, c: 10 } }); // Merge 'Cảm quan' trên 2 cột Đạt, Không đạt

    // Merge cột J và K của dòng tổng (dòng tổng là dòng sau cùng của bảng, tìm theo giá trị "Tổng")
    const tongRowIdx = excelData.findIndex((row) => row[0] === "STT");
    if (tongRowIdx !== -1) {
      // Merge cột J và K (cột 9 và 10, 0-based)
      merges.push({ s: { r: tongRowIdx, c: 9 }, e: { r: tongRowIdx, c: 10 } });
      // Merge dòng tổng và dòng tiếp theo (dòng 13 và 14) các cột từ A đến I (cột 0 đến 8)
      // từng cột 1 chứ không phải tất cả cột từ A đến I

      [0, 1, 2, 3, 4, 5, 6, 7, 8].forEach((colIdx) => {
        merges.push({
          s: { r: tongRowIdx, c: colIdx },
          e: { r: tongRowIdx + 1, c: colIdx },
        });
      });
    }
    worksheet["!merges"] = merges;
    // Set row heights
    worksheet["!rows"] = excelData.map((row) => {
      const text = (row[0] || "").toString();
      if (text.includes("PHIẾU") || text.includes("phiếu")) return { hpt: 25 };
      if (
        row.some((cell) => {
          const str = cell == null ? "" : String(cell);
          return (
            str.includes("STT") ||
            str.includes("Mã") ||
            str.includes("Tên") ||
            str.includes("ĐVT") ||
            str.includes("Số lượng") ||
            str.includes("Đơn giá") ||
            str.includes("Thành tiền") ||
            str.includes("Barcode")
          );
        })
      ) {
        return { hpt: 20 };
      }
      if (
        row.some((cell) => {
          const str = cell == null ? "" : String(cell);
          return (
            str.includes("Người") ||
            str.includes("Thủ") ||
            str.includes("Kế") ||
            str.includes("Giám") ||
            str.includes("Ký")
          );
        })
      ) {
        return { hpt: 20 };
      }
      if (
        row.some((cell) => {
          const str = cell == null ? "" : String(cell);
          return str.includes("Ký, họ tên");
        })
      ) {
        return { hpt: 50 };
      }
      return { hpt: 15 };
    });
    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      loaiPhieu === "xuat"
        ? "Phiếu Xuất Kho"
        : loaiPhieu === "nhapkho"
        ? "Phiếu Nhập Kho"
        : "Phiếu Nhập Hàng"
    );
    // Save file
    const fileName = `${row.RefNo || "phieu"}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  } catch (error) {
    console.error("Lỗi khi xuất file Excel:", error);
    throw error;
  }
};
